import { Component, ElementRef, Inject, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';

import { OrderMenuItemSourceType } from '../../../core/enums/order-menu-item-source-type.enum';

import { Address } from '../../../core/models/address.model';
import { GroceryCategoryInfo, GroceryCategoryItem } from '../../../core/models/grocery-category-info.model';
import { GroceryMenuItem } from '../../../core/models/grocery-menu-item.model';
import { GroceryVendorDetails } from '../../../core/models/grocery-vendor-details.model';
import { NewCartItem } from '../../../core/models/new-cart-item.model';

import { AnalyticsService } from '../../../core/services/analytics.service';
import { CartService } from '../../../core/services/cart.service';
import { DeepLinkService } from '../../../core/services/deep-link.service';
import { SettingsService } from '../../../core/services/settings.service';
import { PlatformService } from '../../../core/services/ssr/platform.service';

import { AnalyticsConfig, TOKEN_ANALYTICS_CONFIG } from '../../../core/config/analytics.config';

import { Helpers } from '../../../core/helpers';

import SwiperCore, { Navigation, SwiperOptions } from 'swiper';

SwiperCore.use([Navigation]);

@Component({
  selector: 'app-grocery-category-items-horizontal-list',
  templateUrl: './grocery-category-items-horizontal-list.component.html',
  styleUrls: ['./grocery-category-items-horizontal-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GroceryCategoryItemsHorizontalListComponent implements OnInit {
  @ViewChild('scrollElement', { read: ElementRef })
  public scrollElement: ElementRef<HTMLElement>;

  @Input()
  public serviceType: number;

  @Input()
  public address: Address;

  @Input()
  public vendorDetails: GroceryVendorDetails;

  @Input()
  public categoryItem: GroceryCategoryItem;

  @Input()
  public parentCategoryInfo?: GroceryCategoryInfo;

  public isBrowser: boolean;
  public browserSliderConfig: SwiperOptions;
  public browserSliderDirection: string;

  public hasMoreItems: boolean;

  constructor(
    private elementRef: ElementRef,
    private cartService: CartService,
    private settingsService: SettingsService,
    private platformService: PlatformService,
    private deepLinkServices: DeepLinkService,
    private analyticsService: AnalyticsService,
    @Inject(TOKEN_ANALYTICS_CONFIG) private analyticsConfig: AnalyticsConfig,
  ) {}

  ngOnInit(): void {
    this.hasMoreItems =
      !this.categoryItem.category.totalItemsCount || this.categoryItem.category.totalItemsCount > this.categoryItem.menuItems.length;

    this.isBrowser = this.platformService.isBrowser;
    this.browserSliderConfig = {
      slidesPerView: 'auto',
      freeMode: true,
      navigation: {
        nextEl: `#next-${this.categoryItem.category.categoryId}`,
        prevEl: `#prev-${this.categoryItem.category.categoryId}`,
      },
    };
    this.browserSliderDirection = this.settingsService.getLanguageDirection();
  }

  public onOpenCategoryDetails(): void {
    this.deepLinkServices.openVendorCategoriesPage(this.vendorDetails.vendorId, {
      selectedServiceType: this.serviceType,
      selectedAddress: this.address,
      vendorDetails: this.vendorDetails,
      activeCategoryInfo: this.categoryItem.category,
      parentCategoryInfo: this.parentCategoryInfo,
    });
  }

  public onMaybeAddItem(item: GroceryMenuItem): void {
    const hasSelectedMaxQuantity = item.selectedQuantity === item.quantity;

    if (!item.inStock || hasSelectedMaxQuantity) {
      return;
    }

    const newQuantity = item.selectedQuantity ? item.selectedQuantity + 1 : 1;

    void this.maybeUpdateMenuItemQuantity(item, newQuantity).then((wasUpdated) => {
      if (wasUpdated) {
        item.selectedQuantity = newQuantity;
      }
    });
  }

  public onRemoveItem(item: GroceryMenuItem): void {
    if (item.selectedQuantity === 0) {
      return;
    }

    const newQuantity = item.selectedQuantity - 1;

    void this.maybeUpdateMenuItemQuantity(item, newQuantity).then((wasUpdated) => {
      if (wasUpdated) {
        item.selectedQuantity = newQuantity;
      }
    });
  }

  public getCategoryId(): number {
    return this.categoryItem.category.categoryId;
  }

  public getScrollElement(): HTMLElement {
    return this.scrollElement?.nativeElement || undefined;
  }

  public getNativeElement(): HTMLElement {
    return (this.elementRef?.nativeElement as HTMLElement) || undefined;
  }

  private maybeUpdateMenuItemQuantity(item: GroceryMenuItem, newQuantity: number): Promise<boolean> {
    if (newQuantity === 0) {
      this.cartService.removeItemsFromCart(this.vendorDetails.vendorId, [item.menuItemId]);
      return Promise.resolve(true);
    }

    const newCartItem: NewCartItem = {
      catererId: this.vendorDetails.vendorId,
      catererName: this.vendorDetails.name,
      minimumOrderValue: this.vendorDetails.minimumOrderValue,
      menuItemId: item.menuItemId,
      menuItemName: item.name,
      serviceType: item.serviceType,
      sourceId: this.vendorDetails.vendorId,
      sourceType: OrderMenuItemSourceType.Vendor,
      imageUrl: item.imageUrl,
      quantity: newQuantity,
      total: Helpers.multiplyWithPrecision(item.price, newQuantity),
      noticePeriod: item.noticePeriodHours,
    };

    return this.cartService
      .maybeAddToCart({
        item: newCartItem,
        address: this.address,
      })
      .then(({ added }) => {
        void this.analyticsService.trackEvent({
          name: this.analyticsConfig.eventName.menuItemDetailsPageAddToCart,
          data: {
            [this.analyticsConfig.propertyName.catererId]: this.vendorDetails.vendorId,
            [this.analyticsConfig.propertyName.menuItemId]: item.menuItemId,
            [this.analyticsConfig.propertyName.amount]: item.price,
            [this.analyticsConfig.propertyName.currency]: this.settingsService.currencySymbolEN,
            [this.analyticsConfig.propertyName.isGroceryItem]: true,
          },
          includeAreaDateTime: true,
        });

        return added;
      });
  }
}
